/**
 * Admin — Theme Designer
 *
 * Full theme token editor with curated groups and a live preview panel.
 * Bidirectional focus: clicking a token group highlights its preview elements,
 * clicking a preview element highlights and scrolls to its token group.
 *
 * English only (admin console).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AdminPages.module.css';
import {
  saveTokenOverrides,
  clearThemeOverrides,
  getOverridesForTheme,
  deriveHighlightTokens,
  // DEV-1478 — per-theme background allowlist, stored in the same override bag.
  getTextureAllowlistRaw,
  saveTextureAllowlist,
} from '../../theme/themeOverrides';
import {
  ALL_TEXTURE_IDS,
  HIRES_TEXTURE_IDS,
  HIRES_TEXTURES,
  texturesForTheme,
  parseTextureAllowlist,
  serializeTextureAllowlist,
} from '../../theme/backgroundTexture';
import type { TextureId, HiresTextureId } from '../../theme/backgroundTexture';
import {
  saveCustomTheme,
  deleteCustomTheme,
  renameCustomTheme,
  getCustomTheme,
  listCustomThemeNames,
  isThemeVisible,
  setThemeVisibility,
  getThemeMetadata,
  setThemeMetadata,
} from '../../theme/themeRegistry';
import type { CustomTheme } from '../../theme/themeRegistry';
import { DARK_THEME } from '../../theme/themes/dark';
import { LIGHT_THEME } from '../../theme/themes/light';
import type { ThemeTokens } from '../../theme/types';
import { useTheme } from '../../theme';

/* ================================================================== */
/*  Token Group Definitions                                           */
/* ================================================================== */

interface TokenDef {
  token: string;
  label: string;
  hint?: string;
  type: 'color' | 'rgba' | 'shadow' | 'duration' | 'easing' | 'text';
}

interface TokenGroup {
  id: string;
  label: string;
  description: string;
  tokens: TokenDef[];
}

const TOKEN_GROUPS: TokenGroup[] = [
  {
    id: 'backgrounds',
    label: 'Backgrounds',
    description: 'Primary surfaces, elevated layers, and overlay backgrounds.',
    tokens: [
      { token: '--bg-primary',   label: 'Primary Background',   hint: 'Main page background (darkest layer)', type: 'color' },
      { token: '--bg-surface',   label: 'Surface Background',   hint: 'Widgets, cards, panels on the page', type: 'rgba' },
      { token: '--bg-secondary', label: 'Secondary Background', hint: 'Legacy alias for surface (sidebar, panels)', type: 'rgba' },
      { token: '--bg-tertiary',  label: 'Tertiary Background',  hint: 'Nested panels, code blocks, raised elements', type: 'color' },
      { token: '--bg-elevated',  label: 'Elevated Background',  hint: 'Frosted overlay panels (modals)', type: 'rgba' },
      { token: '--bg-secondary-header', label: 'Section Header BG', hint: 'Darkened background for panel/section headers', type: 'color' },
    ],
  },
  {
    id: 'text',
    label: 'Text',
    description: 'Text color hierarchy — primary through muted.',
    tokens: [
      { token: '--text-primary',   label: 'Primary Text',   hint: 'Headings, important text', type: 'color' },
      { token: '--text-secondary', label: 'Secondary Text', hint: 'Body text, labels', type: 'color' },
      { token: '--text-tertiary',  label: 'Tertiary Text',  hint: 'Descriptions, hints', type: 'color' },
      { token: '--text-muted',     label: 'Muted Text',     hint: 'Placeholders, disabled', type: 'color' },
    ],
  },
  {
    id: 'accents',
    label: 'Accent / Interactive',
    description: 'Primary accent color family — buttons, links, active states.',
    tokens: [
      { token: '--accent-primary',       label: 'Accent Primary',    hint: 'Buttons, links, active indicators', type: 'color' },
      { token: '--accent-primary-hover',  label: 'Accent Hover',      hint: 'Button hover state', type: 'color' },
      { token: '--accent-secondary',     label: 'Accent Secondary',  hint: 'Secondary accent (tags, badges)', type: 'color' },
      { token: '--accent-bg',            label: 'Accent Background', hint: 'Active item background tint', type: 'rgba' },
      { token: '--accent-bg-40',         label: 'Accent Background Strong', hint: 'Search highlight, focused item emphasis', type: 'rgba' },
      { token: '--accent-border',        label: 'Accent Border',     hint: 'Active item border tint', type: 'rgba' },
      { token: '--accent-text',          label: 'Accent Text',       hint: 'Text on accent backgrounds', type: 'color' },
    ],
  },
  {
    id: 'semantic',
    label: 'Semantic Status',
    description: 'Success, warning, and error colors used throughout the app.',
    tokens: [
      { token: '--color-success',  label: 'Success',          type: 'color' },
      { token: '--color-warning',  label: 'Warning',          type: 'color' },
      { token: '--color-error',    label: 'Error',            type: 'color' },
      { token: '--success-bg',     label: 'Success BG',       type: 'rgba' },
      { token: '--success-border', label: 'Success Border',   type: 'rgba' },
      { token: '--error-bg',       label: 'Error BG',         type: 'rgba' },
      { token: '--error-border',   label: 'Error Border',     type: 'rgba' },
      { token: '--warning-bg',     label: 'Warning BG',       type: 'rgba' },
      { token: '--warning-border', label: 'Warning Border',   type: 'rgba' },
    ],
  },
  {
    id: 'pillars',
    label: 'Pillar Brand Colors',
    description: 'The five Life4Earth pillar identity colors.',
    tokens: [
      { token: '--pillar-problems',    label: 'Problems (Red)',      type: 'color' },
      { token: '--pillar-solutions',   label: 'Solutions (Green)',   type: 'color' },
      { token: '--pillar-enablers',    label: 'Enablers (Blue)',     type: 'color' },
      { token: '--pillar-communities', label: 'Communities (Slate)', type: 'color' },
      { token: '--pillar-projects',    label: 'Projects (Purple)',   type: 'color' },
    ],
  },
  {
    id: 'surfaces',
    label: 'Interactive Surfaces',
    description: 'Hover, active, and pressed state backgrounds.',
    tokens: [
      { token: '--bg-hover',        label: 'Hover Background',        type: 'rgba' },
      { token: '--bg-hover-strong', label: 'Hover Background Strong', type: 'rgba' },
      { token: '--bg-active',       label: 'Active Background',       type: 'rgba' },
    ],
  },
  {
    id: 'borders',
    label: 'Borders',
    description: 'Border hierarchy from subtle to strong.',
    tokens: [
      { token: '--border-subtle',  label: 'Subtle Border',  type: 'rgba' },
      { token: '--border-default', label: 'Default Border', type: 'rgba' },
      { token: '--border-strong',  label: 'Strong Border',  type: 'rgba' },
    ],
  },
  {
    id: 'cards',
    label: 'Cards & Panels',
    description: 'Card backgrounds, borders, scrims, and overlays.',
    tokens: [
      { token: '--bg-surface',       label: 'Surface Background', hint: 'Widgets, panels, cards on the page', type: 'rgba' },
      { token: '--card-bg',          label: 'Card Background',    hint: 'Canonical card chrome — must be SOLID for cross-context consistency', type: 'color' },
      { token: '--card-border',      label: 'Card Border',        type: 'rgba' },
      { token: '--chart-canvas-bg',  label: 'Chart Canvas BG',    hint: 'Data well inside cards — chart panels, sparklines, gauges', type: 'color' },
      { token: '--card-scrim',       label: 'Card Scrim',         hint: 'Bottom text bar on image cards', type: 'rgba' },
      { token: '--card-overlay',     label: 'Card Overlay',       hint: 'Hover overlay revealing description', type: 'rgba' },
      { token: '--card-scrim-blur',  label: 'Card Scrim Blur',    hint: 'Backdrop blur for scrims (e.g. 8px)', type: 'text' },
    ],
  },
  {
    id: 'leftnav',
    label: 'Left Navigation',
    description: 'Sidebar navigation background, item colors, and active state.',
    tokens: [
      { token: '--leftnav-bg',                 label: 'Nav Background',       hint: 'Main sidebar background color', type: 'color' },
      { token: '--leftnav-border',             label: 'Nav Border',           hint: 'Right edge border of the sidebar', type: 'rgba' },
      { token: '--leftnav-item-text',          label: 'Item Text',            hint: 'Default nav item text color', type: 'color' },
      { token: '--leftnav-item-hover',         label: 'Item Hover BG',        hint: 'Background on hover', type: 'rgba' },
      { token: '--leftnav-item-active-bg',     label: 'Active Item BG',       hint: 'Background of the selected nav item', type: 'rgba' },
      { token: '--leftnav-item-active-border', label: 'Active Item Border',   hint: 'Border of the selected nav item', type: 'rgba' },
      { token: '--leftnav-item-active-text',   label: 'Active Item Text',     hint: 'Text color of the selected nav item', type: 'color' },
      { token: '--leftnav-group-label',        label: 'Group Label',          hint: 'Section header text (EXPLORE, ENGAGE, etc.)', type: 'color' },
      { token: '--leftnav-shadow',             label: 'Nav Shadow',           hint: 'Drop shadow on the right edge (depth cue)', type: 'shadow' },
    ],
  },
  {
    // DEV-1459 — sits directly after Left Navigation on purpose: LeftNav and
    // Rail are the two left columns and read as one system (POLICY_NAVIGATION_RAILS).
    // Structural tokens (--rail-width-*, --rail-breakpoint) are deliberately
    // ABSENT — the designer edits colour, never layout.
    id: 'rail',
    label: 'Rail / Context List',
    description: 'The second left column — the context list for the current section (Communities, Resources, Action Center). Per POLICY_NAVIGATION_RAILS the Rail shares one chrome contract; identity comes from its content, not its colors.',
    tokens: [
      { token: '--rail-bg',                 label: 'Rail Background',     hint: 'MUST STAY OPAQUE — the Rail floats over dense page content. A translucent value here makes the page bleed through.', type: 'color' },
      { token: '--rail-border',             label: 'Rail Border',         hint: 'Inline-end edge border of the rail', type: 'rgba' },
      { token: '--rail-shadow',             label: 'Rail Shadow',         hint: 'Drop shadow on the inline-end edge (depth cue)', type: 'shadow' },
      { token: '--rail-item-text',          label: 'Item Text',           hint: 'Default rail row text color', type: 'color' },
      { token: '--rail-item-muted',         label: 'Item Muted Text',     hint: 'Secondary/metadata text within a rail row', type: 'color' },
      { token: '--rail-item-hover',         label: 'Item Hover BG',       hint: 'Background on hover', type: 'rgba' },
      { token: '--rail-item-active-bg',     label: 'Active Item BG',      hint: 'Background of the selected rail row', type: 'rgba' },
      { token: '--rail-item-active-border', label: 'Active Item Border',  hint: 'Border of the selected rail row', type: 'rgba' },
      { token: '--rail-item-active-text',   label: 'Active Item Text',    hint: 'Text color of the selected rail row', type: 'color' },
      { token: '--rail-header-label',       label: 'Header Label',        hint: 'Rail section header text (e.g. COMMUNITIES)', type: 'color' },
    ],
  },
  {
    id: 'topbar',
    label: 'App Menu Bar',
    description: 'Top navigation bar and header glass effects.',
    tokens: [
      { token: '--topbar-shadow',    label: 'Menu Bar Shadow',       hint: 'Drop shadow on the bottom edge', type: 'shadow' },
      { token: '--topbar-bg',        label: 'Menu Bar Background',   hint: 'Main top bar background', type: 'rgba' },
      { token: '--topbar-border',    label: 'Menu Bar Border',       hint: 'Bottom edge border', type: 'rgba' },
      { token: '--header-glass-bg',  label: 'Header Glass BG',       hint: 'Frosted glass panel background', type: 'rgba' },
      { token: '--header-glass-tab', label: 'Header Glass Tab BG',   hint: 'Tab background within glass headers', type: 'rgba' },
    ],
  },
  {
    id: 'shadows',
    label: 'Shadows',
    description: 'Drop shadow hierarchy.',
    tokens: [
      { token: '--shadow-sm',   label: 'Small Shadow',  type: 'shadow' },
      { token: '--shadow-base', label: 'Base Shadow',   type: 'shadow' },
      { token: '--shadow-lg',   label: 'Large Shadow',  type: 'shadow' },
    ],
  },
  {
    id: 'scrollbar',
    label: 'Scrollbar',
    description: 'Custom scrollbar track and thumb colors.',
    tokens: [
      { token: '--scrollbar-track', label: 'Track Color', type: 'color' },
      { token: '--scrollbar-thumb', label: 'Thumb Color', type: 'color' },
    ],
  },
  {
    id: 'globe',
    label: 'Globe / Discover',
    description: 'WebGL shader colors for the Discover globe background, starfield, and glow effects.',
    tokens: [
      { token: '--globe-bg-dark', label: 'Background Edge', type: 'color' },
      { token: '--globe-bg-mid', label: 'Background Center', type: 'color' },
      { token: '--globe-star-color', label: 'Star Tint', type: 'color' },
      { token: '--globe-glow', label: 'Glow / Accent', type: 'color' },
    ],
  },
];

/* ================================================================== */
/*  Reverse map: token name → group id                                */
/* ================================================================== */

const TOKEN_TO_GROUP: Record<string, string> = {};
for (const g of TOKEN_GROUPS) {
  for (const t of g.tokens) {
    TOKEN_TO_GROUP[t.token] = g.id;
  }
}

/* ================================================================== */
/*  Preview section → token mappings                                  */
/*  Each preview section declares which tokens it visually represents */
/* ================================================================== */

interface PreviewSection {
  id: string;
  label: string;
  /** Group IDs that this preview section relates to */
  groups: string[];
  /** Individual tokens used by this preview section */
  tokens: string[];
}

const PREVIEW_SECTIONS: PreviewSection[] = [
  {
    id: 'card',
    label: 'Card & Text',
    groups: ['cards', 'text', 'shadows'],
    tokens: [
      '--bg-surface', '--card-bg', '--card-border', '--shadow-base',
      '--card-scrim', '--card-overlay',
      '--text-primary', '--text-secondary', '--text-tertiary', '--text-muted',
    ],
  },
  {
    id: 'buttons',
    label: 'Buttons',
    groups: ['accents', 'backgrounds', 'borders'],
    tokens: [
      '--accent-primary', '--accent-bg', '--accent-text', '--accent-border',
      '--bg-tertiary', '--text-secondary', '--border-default',
    ],
  },
  {
    id: 'leftnav',
    label: 'Left Navigation',
    groups: ['leftnav'],
    tokens: [
      '--leftnav-bg', '--leftnav-border', '--leftnav-item-text',
      '--leftnav-item-hover', '--leftnav-item-active-bg',
      '--leftnav-item-active-border', '--leftnav-item-active-text',
      '--leftnav-group-label',
    ],
  },
  {
    // DEV-1459 — mirrors the leftnav preview so the bidirectional group↔preview
    // focus linking works for the rail group too.
    id: 'rail',
    label: 'Rail / Context List',
    groups: ['rail'],
    tokens: [
      '--rail-bg', '--rail-border', '--rail-item-text', '--rail-item-muted',
      '--rail-item-hover', '--rail-item-active-bg',
      '--rail-item-active-border', '--rail-item-active-text',
      '--rail-header-label',
    ],
  },
  {
    id: 'tabs',
    label: 'Tab Bar / Project Layers',
    groups: ['accents', 'borders', 'text', 'surfaces', 'topbar'],
    tokens: [
      '--accent-primary', '--text-muted', '--text-secondary', '--text-tertiary',
      '--bg-hover', '--border-subtle', '--header-glass-bg', '--header-glass-tab',
    ],
  },
  {
    id: 'topbar',
    label: 'Topbar',
    groups: ['topbar', 'accents'],
    tokens: [
      '--topbar-bg', '--topbar-border', '--accent-primary',
      '--text-primary', '--text-muted',
    ],
  },
  {
    id: 'status',
    label: 'Status Badges',
    groups: ['semantic'],
    tokens: [
      '--success-bg', '--color-success', '--success-border',
      '--warning-bg', '--color-warning', '--warning-border',
      '--error-bg', '--color-error', '--error-border',
    ],
  },
  {
    id: 'pillars',
    label: 'Pillar Colors',
    groups: ['pillars'],
    tokens: [
      '--pillar-problems', '--pillar-solutions', '--pillar-enablers',
      '--pillar-communities', '--pillar-projects',
    ],
  },
  {
    id: 'forms',
    label: 'Form Elements',
    groups: ['backgrounds', 'borders', 'accents'],
    tokens: [
      '--bg-tertiary', '--border-subtle', '--text-muted',
      '--bg-primary', '--border-default', '--text-primary',
      '--accent-primary', '--accent-bg',
    ],
  },
  {
    id: 'glass',
    label: 'Glass Header',
    groups: ['topbar'],
    tokens: [
      '--header-glass-bg', '--border-subtle', '--text-primary', '--text-tertiary',
    ],
  },
  {
    id: 'projectCard',
    label: 'Project Card',
    groups: ['cards', 'text', 'semantic', 'shadows', 'borders'],
    tokens: [
      '--card-bg', '--card-border', '--card-scrim', '--card-overlay', '--shadow-base',
      '--text-primary', '--text-secondary', '--text-muted',
      '--color-success', '--color-warning', '--border-subtle',
      '--pillar-projects',
    ],
  },
  {
    id: 'communityCard',
    label: 'Community Card',
    groups: ['cards', 'text', 'pillars', 'shadows'],
    tokens: [
      '--card-bg', '--card-border', '--shadow-base',
      '--text-primary', '--text-secondary', '--text-muted',
      '--pillar-communities', '--border-subtle',
    ],
  },
];

/* DEV-1459 — look preview sections up by id, not by array position. The preview
   render previously hand-indexed PREVIEW_SECTIONS[0..10], so inserting a section
   mid-array (as the rail one is) would silently mislabel every section after it
   and break the focus linking. */
const PS = (id: string): PreviewSection => {
  const hit = PREVIEW_SECTIONS.find((s) => s.id === id);
  if (!hit) throw new Error(`Unknown preview section: ${id}`);
  return hit;
};

/* ================================================================== */
/*  Highlight ring style (shared)                                     */
/* ================================================================== */

const HIGHLIGHT_RING = '0 0 0 2px #3b82f6, 0 0 8px rgba(59,130,246,0.35)';
const HIGHLIGHT_RING_PREVIEW = '0 0 0 2px #f59e0b, 0 0 8px rgba(245,158,11,0.35)';

/* ================================================================== */
/*  Utility: detect if value looks like a hex color                   */
/* ================================================================== */

function isHexColor(v: string): boolean {
  return /^#[0-9a-f]{3,8}$/i.test(v.trim());
}

function extractRgbaComponents(v: string): { r: number; g: number; b: number; a: number } | null {
  const m = v.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/);
  if (!m) return null;
  return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
}

function rgbaToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
}

/* ================================================================== */
/*  Token Editor Row                                                  */
/* ================================================================== */

function TokenRow({
  def,
  value,
  baseValue,
  isFocused,
  onChange,
  onReset,
  rowRef,
}: {
  def: TokenDef;
  value: string;
  baseValue: string;
  isFocused: boolean;
  onChange: (token: string, value: string) => void;
  onReset: (token: string) => void;
  rowRef?: React.Ref<HTMLDivElement>;
}) {
  const isOverridden = value !== baseValue;

  const colorPickerValue = useMemo(() => {
    if (isHexColor(value)) return value;
    const rgba = extractRgbaComponents(value);
    if (rgba) return rgbaToHex(rgba.r, rgba.g, rgba.b);
    return '#000000';
  }, [value]);

  const showColorPicker = def.type === 'color' || def.type === 'rgba';

  return (
    <div
      ref={rowRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 4px',
        borderBottom: '1px solid #f0f0f0',
        borderRadius: 6,
        transition: 'background 0.25s ease, box-shadow 0.25s ease',
        background: isFocused ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
        boxShadow: isFocused ? HIGHLIGHT_RING_PREVIEW : 'none',
      }}
    >
      {/* Color swatch */}
      <div style={{
        width: 28, height: 28, borderRadius: 6,
        background: value, border: '1px solid #d1d5db', flexShrink: 0,
      }} />

      {/* Label + token name */}
      <div style={{ flex: '0 0 180px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', lineHeight: 1.3 }}>
          {def.label}
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>
          {def.token}
        </div>
        {def.hint && (
          <div style={{ fontSize: 10, color: '#b0b5bd', marginTop: 1 }}>{def.hint}</div>
        )}
      </div>

      {/* Value editor */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
        {showColorPicker && (
          <input
            type="color"
            value={colorPickerValue}
            onChange={(e) => {
              if (def.type === 'rgba') {
                const rgba = extractRgbaComponents(value);
                const r = parseInt(e.target.value.slice(1, 3), 16);
                const g = parseInt(e.target.value.slice(3, 5), 16);
                const b = parseInt(e.target.value.slice(5, 7), 16);
                const a = rgba ? rgba.a : 1;
                onChange(def.token, a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : e.target.value);
              } else {
                onChange(def.token, e.target.value);
              }
            }}
            style={{
              width: 32, height: 28, border: '1px solid #d1d5db',
              borderRadius: 4, cursor: 'pointer', padding: 2, flexShrink: 0,
            }}
          />
        )}
        {/* Opacity slider for rgba tokens */}
        {def.type === 'rgba' && (() => {
          const rgba = extractRgbaComponents(value);
          const alpha = rgba ? rgba.a : 1;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={alpha}
                onChange={(e) => {
                  const newAlpha = parseFloat(e.target.value);
                  const comps = rgba || { r: 0, g: 0, b: 0, a: 1 };
                  onChange(def.token, `rgba(${comps.r}, ${comps.g}, ${comps.b}, ${newAlpha})`);
                }}
                style={{ width: 60, accentColor: '#3b82f6' }}
                title={`Opacity: ${Math.round(alpha * 100)}%`}
              />
              <span style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace', width: 32, textAlign: 'right' as const }}>
                {Math.round(alpha * 100)}%
              </span>
            </div>
          );
        })()}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(def.token, e.target.value)}
          spellCheck={false}
          style={{
            flex: 1, padding: '5px 8px', fontSize: 12, fontFamily: 'monospace',
            border: `1px solid ${isOverridden ? '#3b82f6' : '#e5e7eb'}`,
            borderRadius: 5, background: isOverridden ? '#eff6ff' : '#f9fafb',
            color: '#1a1a2e', minWidth: 80,
          }}
        />
      </div>

      {/* Reset button */}
      {isOverridden && (
        <button
          type="button"
          onClick={() => onReset(def.token)}
          title={`Reset to default: ${baseValue}`}
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 600, color: '#6b7280',
            background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4,
            cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' as const,
          }}
        >
          Reset
        </button>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Background Allowlist Panel (DEV-1478)                             */
/* ================================================================== */

/**
 * Which background textures a theme offers its users.
 *
 * Two states, and the difference matters:
 *   NOT CONFIGURED — the code defaults apply (Hex Glow Black → Ninja, Blue →
 *                    Dark, SVG patterns → everything). Shown as "Using defaults".
 *   CONFIGURED     — this theme's explicit list wins, even if it's empty.
 * Collapsing those two into one would make it impossible to hand a theme back to
 * the defaults once you'd touched it, so "Reset to defaults" deletes the key
 * rather than writing an empty list.
 *
 * Saves immediately (not on the page's Save button) because it writes to a
 * different part of the override bag than the token editor, and interleaving the
 * two would let one clobber the other's unsaved state.
 *
 * Solid is deliberately absent from the list: a user must always be able to turn
 * a background off, so it is never restrictable.
 */
function BackgroundAllowlistPanel({
  themeName,
  baseTheme,
}: {
  themeName: string;
  baseTheme: 'dark' | 'light';
}) {
  const stored = getTextureAllowlistRaw(themeName);
  const configured = stored != null;

  // What the theme offers right now — either its explicit list, or the defaults.
  const effective = useMemo(
    () => texturesForTheme(baseTheme, themeName, parseTextureAllowlist(stored)),
    [baseTheme, themeName, stored],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set(effective));
  const [saved, setSaved] = useState(false);

  // Re-seed when the edited theme changes.
  useEffect(() => {
    setSelected(new Set(effective));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName]);

  // Solid is always on offer and is not shown as a choice.
  const choosable = ALL_TEXTURE_IDS.filter((id) => id !== 'solid');

  const commit = (next: Set<string>) => {
    setSelected(next);
    saveTextureAllowlist(
      themeName,
      serializeTextureAllowlist([...next] as TextureId[]),
    );
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    commit(next);
  };

  const resetToDefaults = () => {
    saveTextureAllowlist(themeName, null);   // delete the key → defaults return
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSelected(new Set(texturesForTheme(baseTheme, themeName, null)));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid var(--border-subtle)',
        borderRadius: 10,
        padding: 16,
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          Backgrounds
        </div>
        <span
          style={{
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 999,
            background: configured ? 'var(--accent-bg)' : 'var(--bg-tertiary)',
            color: configured ? 'var(--accent-text)' : 'var(--text-muted)',
            border: `1px solid ${configured ? 'var(--accent-border)' : 'var(--border-default)'}`,
          }}
        >
          {configured ? 'Custom' : 'Using defaults'}
        </span>
        {saved && (
          <span style={{ fontSize: 11, color: 'var(--color-success)' }}>Saved</span>
        )}
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Which backgrounds users may pick when this theme is active. Solid is always
        available and cannot be removed.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {choosable.map((id) => {
          const isHires = HIRES_TEXTURE_IDS.includes(id as HiresTextureId);
          return (
            <label
              key={id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(id)}
                onChange={() => toggle(id)}
              />
              <span style={{ flex: 1 }}>{id}</span>
              {isHires && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {HIRES_TEXTURES[id as HiresTextureId]?.sizeLabel} image
                </span>
              )}
            </label>
          );
        })}
      </div>

      {configured && (
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={resetToDefaults}
          style={{ marginTop: 12, padding: '5px 12px', fontSize: 11 }}
        >
          Reset to defaults
        </button>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Token Group Panel (collapsible)                                   */
/* ================================================================== */

function TokenGroupPanel({
  group,
  editedTokens,
  baseTheme,
  focusedGroupId,
  focusedTokens,
  onChange,
  onReset,
  onGroupClick,
  groupRef,
  tokenRefs,
}: {
  group: TokenGroup;
  editedTokens: ThemeTokens;
  baseTheme: ThemeTokens;
  focusedGroupId: string | null;
  focusedTokens: Set<string>;
  onChange: (token: string, value: string) => void;
  onReset: (token: string) => void;
  onGroupClick: (groupId: string) => void;
  groupRef: React.Ref<HTMLDivElement>;
  tokenRefs: Record<string, React.RefObject<HTMLDivElement>>;
}) {
  const isFocused = focusedGroupId === group.id;
  const hasAnyFocusedToken = group.tokens.some((t) => focusedTokens.has(t.token));

  // Auto-expand when focused from preview click
  const [expanded, setExpanded] = useState(true);
  useEffect(() => {
    if (isFocused || hasAnyFocusedToken) setExpanded(true);
  }, [isFocused, hasAnyFocusedToken]);

  const overrideCount = group.tokens.filter(
    (t) => editedTokens[t.token] !== baseTheme[t.token]
  ).length;

  return (
    <div
      ref={groupRef}
      style={{
        background: '#ffffff',
        border: `1px solid ${isFocused || hasAnyFocusedToken ? '#f59e0b' : '#e5e7eb'}`,
        borderRadius: 8,
        marginBottom: 12,
        overflow: 'hidden',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        boxShadow: isFocused || hasAnyFocusedToken ? HIGHLIGHT_RING_PREVIEW : 'none',
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          // Toggle expand/collapse OR focus from editor→preview
          if (e.ctrlKey || e.metaKey) {
            // Ctrl/Cmd+click = focus link to preview
            onGroupClick(group.id);
          } else {
            setExpanded(!expanded);
          }
        }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', border: 'none',
          background: expanded ? '#f8fafc' : '#ffffff',
          cursor: 'pointer', textAlign: 'start' as const,
        }}
      >
        <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0, width: 16, textAlign: 'center' as const }}>
          {expanded ? '▾' : '▸'}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', flex: 1 }}>
          {group.label}
        </span>
        {/* Focus link button */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onGroupClick(group.id);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.stopPropagation(); onGroupClick(group.id); }
          }}
          title="Highlight in preview"
          style={{
            fontSize: 14, cursor: 'pointer', opacity: 0.5, padding: '0 4px',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = '0.5'; }}
        >
          ◉
        </span>
        {overrideCount > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: '#3b82f6', color: '#ffffff',
          }}>
            {overrideCount} modified
          </span>
        )}
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {group.tokens.length} tokens
        </span>
      </button>
      {expanded && (
        <div style={{ padding: '4px 16px 12px' }}>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af' }}>
            {group.description}
          </p>
          {group.tokens.map((def) => (
            <TokenRow
              key={def.token}
              def={def}
              value={editedTokens[def.token] || ''}
              baseValue={baseTheme[def.token] || ''}
              isFocused={focusedTokens.has(def.token)}
              onChange={onChange}
              onReset={onReset}
              rowRef={tokenRefs[def.token]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Preview Section Wrapper (clickable, highlightable)                */
/* ================================================================== */

function PreviewSectionWrap({
  section,
  isFocused,
  onClick,
  children,
}: {
  section: PreviewSection;
  isFocused: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      title={`Click to highlight: ${section.label}`}
      style={{
        cursor: 'pointer',
        borderRadius: 8,
        transition: 'box-shadow 0.25s ease, outline-color 0.25s ease',
        boxShadow: isFocused ? HIGHLIGHT_RING : 'none',
        outline: isFocused ? '2px solid transparent' : 'none',
        position: 'relative' as const,
      }}
    >
      {/* Floating label when focused */}
      {isFocused && (
        <div style={{
          position: 'absolute' as const, top: -8, insetInlineEnd: 8,
          padding: '1px 8px', borderRadius: 4,
          background: '#3b82f6', color: '#fff',
          fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
          zIndex: 2, pointerEvents: 'none' as const,
        }}>
          {section.label}
        </div>
      )}
      {children}
    </div>
  );
}

/* ================================================================== */
/*  Live Preview Panel                                                */
/* ================================================================== */

function PreviewPanel({
  tokens,
  focusedSectionId,
  onSectionClick,
}: {
  tokens: ThemeTokens;
  focusedSectionId: string | null;
  onSectionClick: (sectionId: string) => void;
}) {
  const t = (key: string) => tokens[key] || '';
  const isF = (id: string) => focusedSectionId === id;

  return (
    <div style={{
      background: t('--bg-primary'),
      borderRadius: 10,
      border: `1px solid ${t('--border-default')}`,
      padding: 20,
      minHeight: 500,
      transition: 'all 0.2s ease',
    }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: t('--text-primary') }}>
        Live Preview
      </h4>
      <p style={{ margin: '0 0 16px', fontSize: 10, color: t('--text-muted') }}>
        Click any section to highlight its tokens on the left.
      </p>

      {/* ── Card & Surface samples ── */}
      <PreviewSectionWrap section={PS('card')} isFocused={isF('card')} onClick={() => onSectionClick('card')}>
        {/* Surface card (widgets like Timeline, Recent Activity) */}
        <div style={{
          background: t('--bg-surface'),
          border: `1px solid ${t('--card-border')}`,
          borderInlineStart: `3px solid ${t('--border-strong') || '#6b7280'}`,
          borderRadius: 8, padding: 14, marginBottom: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t('--text-secondary'), textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 6 }}>
            Surface Widget
          </div>
          <div style={{ fontSize: 12, color: t('--text-secondary') }}>
            Timeline, Recent Activity, What&apos;s Needed, etc.
          </div>
        </div>
        {/* Floating card */}
        <div style={{
          background: t('--card-bg'),
          border: `1px solid ${t('--card-border')}`,
          borderRadius: 8, padding: 14, marginBottom: 10,
          boxShadow: t('--shadow-base'),
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t('--text-primary'), marginBottom: 4 }}>
            Floating Card
          </div>
          <div style={{ fontSize: 12, color: t('--text-secondary') }}>
            Secondary text inside a card.
          </div>
          <div style={{ fontSize: 11, color: t('--text-muted'), marginTop: 4 }}>
            Muted placeholder content.
          </div>
        </div>
        {/* Card scrim / overlay sample */}
        <div style={{
          position: 'relative' as const, height: 60, borderRadius: 8,
          overflow: 'hidden', marginBottom: 16,
          background: 'linear-gradient(135deg, #334155, #1e293b)',
        }}>
          <div style={{
            position: 'absolute' as const, bottom: 0, left: 0, right: 0,
            background: t('--card-scrim'),
            backdropFilter: `blur(${t('--card-scrim-blur') || '8px'})`,
            padding: '6px 10px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>Card Scrim</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>Bottom bar on image cards</div>
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Buttons ── */}
      <PreviewSectionWrap section={PS('buttons')} isFocused={isF('buttons')} onClick={() => onSectionClick('buttons')}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 16 }}>
          <div style={{
            padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: t('--accent-primary'), color: '#fff',
          }}>
            Primary Button
          </div>
          <div style={{
            padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: t('--accent-bg'), color: t('--accent-text'),
            border: `1px solid ${t('--accent-border')}`,
          }}>
            Secondary Button
          </div>
          <div style={{
            padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: t('--bg-tertiary'), color: t('--text-secondary'),
            border: `1px solid ${t('--border-default')}`,
          }}>
            Tertiary
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Left Navigation ── */}
      <PreviewSectionWrap section={PS('leftnav')} isFocused={isF('leftnav')} onClick={() => onSectionClick('leftnav')}>
        <div style={{
          background: t('--leftnav-bg'), borderRadius: 8, padding: 12,
          marginBottom: 16, border: `1px solid ${t('--leftnav-border')}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t('--leftnav-group-label'), marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>
            Explore
          </div>
          {['My Impact', 'Discover', 'Communities', 'Projects'].map((item, idx) => (
            <div key={item} style={{
              padding: '8px 12px', borderRadius: 10, fontSize: 12,
              fontWeight: idx === 0 ? 600 : 400, marginBottom: 4,
              background: idx === 0 ? t('--leftnav-item-active-bg') : idx === 2 ? t('--leftnav-item-hover') : 'transparent',
              border: idx === 0 ? `1px solid ${t('--leftnav-item-active-border')}` : '1px solid transparent',
              color: idx === 0 ? t('--leftnav-item-active-text') : t('--leftnav-item-text'),
            }}>
              {item}
            </div>
          ))}
          <div style={{ fontSize: 11, fontWeight: 700, color: t('--leftnav-group-label'), marginTop: 12, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>
            Engage
          </div>
          {['Messages', 'Approvals'].map((item) => (
            <div key={item} style={{
              padding: '8px 12px', borderRadius: 10, fontSize: 12,
              fontWeight: 400, marginBottom: 4,
              background: 'transparent',
              border: '1px solid transparent',
              color: t('--leftnav-item-text'),
            }}>
              {item}
            </div>
          ))}
        </div>
      </PreviewSectionWrap>

      {/* ── Rail / Context List (DEV-1459) ──
          Mirrors the LeftNav mock above on purpose: header label + rows, one
          hovered, one active. The two left columns are edited side by side, so
          an admin can see them agree (or clash) without leaving the designer. */}
      <PreviewSectionWrap section={PS('rail')} isFocused={isF('rail')} onClick={() => onSectionClick('rail')}>
        <div style={{
          background: t('--rail-bg'), borderRadius: 8, padding: 12,
          marginBottom: 16,
          border: `1px solid ${t('--rail-border')}`,
          boxShadow: t('--rail-shadow'),
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t('--rail-header-label'), marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>
            Communities
          </div>
          {[
            { name: 'Hampton Rotary Club', meta: 'Secretary' },
            { name: 'Boston', meta: 'Member' },
            { name: 'Rotary District 7870', meta: 'Member' },
          ].map((row, idx) => (
            <div key={row.name} style={{
              padding: '8px 12px', borderRadius: 10, marginBottom: 4,
              background: idx === 0 ? t('--rail-item-active-bg') : idx === 2 ? t('--rail-item-hover') : 'transparent',
              border: idx === 0 ? `1px solid ${t('--rail-item-active-border')}` : '1px solid transparent',
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: idx === 0 ? 600 : 400,
                color: idx === 0 ? t('--rail-item-active-text') : t('--rail-item-text'),
              }}>
                {row.name}
              </div>
              <div style={{ fontSize: 11, color: t('--rail-item-muted'), marginTop: 2 }}>
                {row.meta}
              </div>
            </div>
          ))}
        </div>
      </PreviewSectionWrap>

      {/* ── Tab bar / Project Layers ── */}
      <PreviewSectionWrap section={PS('tabs')} isFocused={isF('tabs')} onClick={() => onSectionClick('tabs')}>
        <div style={{
          background: t('--header-glass-bg'), borderRadius: 8,
          border: `1px solid ${t('--border-subtle')}`,
          marginBottom: 16, overflow: 'hidden',
          backdropFilter: 'blur(10px)',
        }}>
          {/* Main layer tabs (Core, Expert, Community, Work, Impact, Media) */}
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: `2px solid ${t('--border-subtle')}`,
            padding: '0 4px',
          }}>
            {['Core', 'Expert', 'Community', 'Work', 'Impact', 'Media'].map((tab, idx) => {
              const isActive = idx === 3; // Work active
              return (
                <div key={tab} style={{
                  padding: '9px 10px', fontSize: 11, fontWeight: isActive ? 700 : 400,
                  color: isActive ? t('--accent-primary') : t('--text-muted'),
                  borderBottom: isActive ? `2px solid ${t('--accent-primary')}` : '2px solid transparent',
                  marginBottom: -2,
                  transition: 'color 150ms, background 150ms',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'default',
                }}>
                  {tab}
                </div>
              );
            })}
          </div>
          {/* Sub-tabs (Performance, Resources, Gantt Chart) */}
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: `1px solid ${t('--border-subtle')}`,
            padding: '0 8px',
            background: t('--header-glass-tab'),
          }}>
            {['Performance', 'Resources', 'Gantt Chart'].map((sub, idx) => {
              const isActive = idx === 0;
              return (
                <div key={sub} style={{
                  padding: '7px 12px', fontSize: 10, fontWeight: isActive ? 600 : 400,
                  color: isActive ? t('--accent-primary') : t('--text-tertiary'),
                  borderBottom: isActive ? `2px solid ${t('--accent-primary')}` : '2px solid transparent',
                  marginBottom: -1,
                }}>
                  {sub}
                </div>
              );
            })}
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Topbar sample ── */}
      <PreviewSectionWrap section={PS('topbar')} isFocused={isF('topbar')} onClick={() => onSectionClick('topbar')}>
        <div style={{
          background: t('--topbar-bg'),
          border: `1px solid ${t('--topbar-border')}`,
          borderRadius: 8, padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 16,
        }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: t('--accent-primary') }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: t('--text-primary'), flex: 1 }}>Life4Earth</span>
          <span style={{ fontSize: 11, color: t('--text-muted') }}>Skip Davis</span>
        </div>
      </PreviewSectionWrap>

      {/* ── Status badges ── */}
      <PreviewSectionWrap section={PS('status')} isFocused={isF('status')} onClick={() => onSectionClick('status')}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 16 }}>
          <span style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: t('--success-bg'), color: t('--color-success'),
            border: `1px solid ${t('--success-border')}`,
          }}>
            On Track
          </span>
          <span style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: t('--warning-bg'), color: t('--color-warning'),
            border: `1px solid ${t('--warning-border')}`,
          }}>
            At Risk
          </span>
          <span style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: t('--error-bg'), color: t('--color-error'),
            border: `1px solid ${t('--error-border')}`,
          }}>
            Critical
          </span>
        </div>
      </PreviewSectionWrap>

      {/* ── Pillar colors ── */}
      <PreviewSectionWrap section={PS('pillars')} isFocused={isF('pillars')} onClick={() => onSectionClick('pillars')}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t('--text-muted'), marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
            Pillar Colors
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { token: '--pillar-problems', label: 'P' },
              { token: '--pillar-solutions', label: 'S' },
              { token: '--pillar-enablers', label: 'E' },
              { token: '--pillar-communities', label: 'C' },
              { token: '--pillar-projects', label: 'Pr' },
            ].map((p) => (
              <div key={p.token} style={{
                width: 32, height: 32, borderRadius: 6,
                background: t(p.token),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: '#fff',
              }}>
                {p.label}
              </div>
            ))}
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Form elements ── */}
      <PreviewSectionWrap section={PS('forms')} isFocused={isF('forms')} onClick={() => onSectionClick('forms')}>
        <div style={{
          background: t('--bg-tertiary'), borderRadius: 8, padding: 12,
          border: `1px solid ${t('--border-subtle')}`,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t('--text-muted'), marginBottom: 6, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
            Form Elements
          </div>
          <div style={{
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            background: t('--bg-primary'),
            border: `1px solid ${t('--border-default')}`,
            color: t('--text-primary'), marginBottom: 8,
          }}>
            Sample input value
          </div>
          <div style={{
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            background: t('--bg-primary'),
            border: `1px solid ${t('--accent-primary')}`,
            color: t('--text-primary'),
            boxShadow: `0 0 0 2px ${t('--accent-bg')}`,
          }}>
            Focused input
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Glass header ── */}
      <PreviewSectionWrap section={PS('glass')} isFocused={isF('glass')} onClick={() => onSectionClick('glass')}>
        <div style={{
          background: t('--header-glass-bg'), borderRadius: 8,
          padding: '10px 16px',
          border: `1px solid ${t('--border-subtle')}`,
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t('--text-primary') }}>
            Glass Header Effect
          </div>
          <div style={{ fontSize: 11, color: t('--text-tertiary') }}>
            Backdrop blur over content
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Project card preview ── */}
      <PreviewSectionWrap section={PS('projectCard')} isFocused={isF('projectCard')} onClick={() => onSectionClick('projectCard')}>
        <div style={{
          background: t('--card-bg'), borderRadius: 10, overflow: 'hidden',
          border: `1px solid ${t('--card-border')}`,
          boxShadow: t('--shadow-base'), marginTop: 16, marginBottom: 16,
        }}>
          {/* Hero image placeholder */}
          <div style={{
            height: 80,
            background: `linear-gradient(135deg, ${t('--pillar-projects')}44, ${t('--accent-primary')}33)`,
            display: 'flex', alignItems: 'flex-end', padding: '0 12px 8px',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
              background: 'rgba(0,0,0,0.45)', color: '#fff',
            }}>
              CONTRIBUTOR
            </span>
            <span style={{
              marginInlineStart: 'auto', fontSize: 9, fontWeight: 600,
              padding: '2px 8px', borderRadius: 4,
              background: 'rgba(0,0,0,0.45)', color: '#fff',
            }}>
              Rhode Island
            </span>
          </div>
          {/* Card body */}
          <div style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t('--text-primary'), marginBottom: 4 }}>
              Community Garden Initiative
            </div>
            <div style={{ fontSize: 11, color: t('--text-secondary'), marginBottom: 8, lineHeight: 1.4 }}>
              A demonstration project to showcase how project cards appear in the current theme.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: t('--color-success'), display: 'inline-block',
              }} />
              <span style={{ fontSize: 10, color: t('--text-muted') }}>In Progress</span>
            </div>
          </div>
        </div>
      </PreviewSectionWrap>

      {/* ── Community card preview ── */}
      <PreviewSectionWrap section={PS('communityCard')} isFocused={isF('communityCard')} onClick={() => onSectionClick('communityCard')}>
        <div style={{
          background: t('--card-bg'), borderRadius: 10, overflow: 'hidden',
          border: `1px solid ${t('--card-border')}`,
          boxShadow: t('--shadow-base'), marginBottom: 16,
        }}>
          {/* Community header band */}
          <div style={{
            height: 56,
            background: `linear-gradient(135deg, ${t('--pillar-communities')}55, ${t('--pillar-communities')}22)`,
            display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: t('--pillar-communities'),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: '#fff', fontWeight: 700, flexShrink: 0,
            }}>
              M
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                Massachusetts
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.75)' }}>
                United States
              </div>
            </div>
          </div>
          {/* Card body */}
          <div style={{ padding: '10px 14px' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: t('--text-muted') }}>
                <span style={{ fontWeight: 600, color: t('--text-secondary') }}>3</span> Projects
              </div>
              <div style={{ fontSize: 10, color: t('--text-muted') }}>
                <span style={{ fontWeight: 600, color: t('--text-secondary') }}>12</span> Members
              </div>
            </div>
            <div style={{
              fontSize: 10, color: t('--text-tertiary'),
              borderTop: `1px solid ${t('--border-subtle')}`,
              paddingTop: 6, marginTop: 4,
            }}>
              Geographic community with active sustainability projects
            </div>
          </div>
        </div>
      </PreviewSectionWrap>
    </div>
  );
}

/* ================================================================== */
/*  Theme base map                                                    */
/* ================================================================== */

const BUILTIN_MAP: Record<string, ThemeTokens> = {
  dark: DARK_THEME,
  light: LIGHT_THEME,
};

function resolveBase(name: string): ThemeTokens {
  if (BUILTIN_MAP[name]) return BUILTIN_MAP[name];
  const custom = getCustomTheme(name);
  if (custom) return BUILTIN_MAP[custom.baseTheme] || BUILTIN_MAP['dark'];
  return BUILTIN_MAP['dark'];
}

function resolveBaseLabel(name: string): 'dark' | 'light' {
  const custom = getCustomTheme(name);
  return custom ? custom.baseTheme : (name as 'dark' | 'light');
}

/* ================================================================== */
/*  Main Page                                                         */
/* ================================================================== */

export default function AdminThemeDesigner() {
  const { themeName: activeTheme, setThemeName: applyTheme } = useTheme();
  const [editingTheme, setEditingTheme] = useState<string>(activeTheme);
  const [editedTokens, setEditedTokens] = useState<ThemeTokens>({});
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const loadedRef = useRef(false);

  /* ── Custom theme management state ── */
  const [customThemes, setCustomThemes] = useState<string[]>(listCustomThemeNames());
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [showRename, setShowRename] = useState(false);
  const [renameTo, setRenameTo] = useState('');

  /* ── Display name & icon state ── */
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editIcon, setEditIcon] = useState('');

  const refreshCustomThemes = useCallback(() => {
    setCustomThemes(listCustomThemeNames());
  }, []);

  /* ── Focus / linking state ── */
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null);
  const [focusedTokens, setFocusedTokens] = useState<Set<string>>(new Set());
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Refs for scroll-to ── */
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const tokenRowRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  // Initialize token row refs
  useMemo(() => {
    for (const g of TOKEN_GROUPS) {
      for (const t of g.tokens) {
        if (!tokenRowRefs.current[t.token]) {
          tokenRowRefs.current[t.token] = React.createRef<HTMLDivElement>();
        }
      }
    }
  }, []);

  const isCustom = !!getCustomTheme(editingTheme);
  const baseTheme = resolveBase(editingTheme);

  // Clear focus after a timeout
  const scheduleClearFocus = useCallback(() => {
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      setFocusedGroupId(null);
      setFocusedSectionId(null);
      setFocusedTokens(new Set());
    }, 4000);
  }, []);

  /* ── Editor → Preview ── */
  const handleGroupFocus = useCallback((groupId: string) => {
    const matchingSections = PREVIEW_SECTIONS.filter((s) => s.groups.includes(groupId));
    if (matchingSections.length > 0) setFocusedSectionId(matchingSections[0].id);
    setFocusedGroupId(groupId);
    const group = TOKEN_GROUPS.find((g) => g.id === groupId);
    if (group) setFocusedTokens(new Set(group.tokens.map((t) => t.token)));
    scheduleClearFocus();
  }, [scheduleClearFocus]);

  /* ── Preview → Editor ── */
  const handlePreviewSectionClick = useCallback((sectionId: string) => {
    const section = PREVIEW_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    setFocusedSectionId(sectionId);
    setFocusedTokens(new Set(section.tokens));
    const primaryGroupId = section.groups[0];
    if (primaryGroupId) {
      setFocusedGroupId(primaryGroupId);
      const el = groupRefs.current[primaryGroupId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    scheduleClearFocus();
  }, [scheduleClearFocus]);

  // Load current state for a theme
  const loadEditState = useCallback((theme: string) => {
    const base = resolveBase(theme);
    const custom = getCustomTheme(theme);
    let merged = { ...base };

    if (custom) {
      // Custom theme: apply its stored overrides
      if (custom.tokenOverrides) {
        merged = { ...merged, ...custom.tokenOverrides };
      }
    } else {
      // Built-in: apply any admin overrides
      const overrides = getOverridesForTheme(theme);
      if (overrides.highlightColor) {
        merged = { ...merged, ...deriveHighlightTokens(overrides.highlightColor) };
      }
      if (overrides.tokenOverrides) {
        merged = { ...merged, ...overrides.tokenOverrides };
      }
    }
    setEditedTokens(merged);
    setDirty(false);
  }, []);

  useEffect(() => {
    loadEditState(editingTheme);
    loadedRef.current = true;
    // Load metadata for display name / icon
    const meta = getThemeMetadata(editingTheme);
    setEditDisplayName(meta?.displayName || '');
    setEditIcon(meta?.icon || '');
  }, [editingTheme, loadEditState]);

  const handleTokenChange = useCallback((token: string, value: string) => {
    setEditedTokens((prev) => ({ ...prev, [token]: value }));
    setDirty(true);
  }, []);

  const handleTokenReset = useCallback((token: string) => {
    setEditedTokens((prev) => ({ ...prev, [token]: baseTheme[token] }));
    setDirty(true);
  }, [baseTheme]);

  // Compute override tokens (diff from base)
  const overrideTokens = useMemo(() => {
    const result: Record<string, string> = {};
    for (const [key, val] of Object.entries(editedTokens)) {
      if (val !== baseTheme[key]) {
        result[key] = val;
      }
    }
    return result;
  }, [editedTokens, baseTheme]);

  const totalOverrides = Object.keys(overrideTokens).length;

  /* ── Save (overwrites current theme) ── */
  const handleSave = useCallback(() => {
    if (isCustom) {
      // Update the custom theme's tokenOverrides
      const existing = getCustomTheme(editingTheme)!;
      saveCustomTheme({ ...existing, tokenOverrides: overrideTokens });
    } else {
      // Built-in: save as token overrides
      saveTokenOverrides(editingTheme, overrideTokens);
    }
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  }, [editingTheme, overrideTokens, isCustom]);

  /* ── Save As (create new named theme) ── */
  const handleSaveAs = useCallback(() => {
    const name = saveAsName.trim();
    if (!name) return;
    // Prevent overwriting built-in names
    if (name === 'dark' || name === 'light') return;

    const newTheme: CustomTheme = {
      name,
      baseTheme: resolveBaseLabel(editingTheme),
      tokenOverrides: overrideTokens,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    saveCustomTheme(newTheme);
    setThemeVisibility(name, true);
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    refreshCustomThemes();
    setEditingTheme(name);
    setShowSaveAs(false);
    setSaveAsName('');
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  }, [saveAsName, editingTheme, overrideTokens, refreshCustomThemes]);

  /* ── Rename current custom theme ── */
  const handleRename = useCallback(() => {
    const newName = renameTo.trim();
    if (!newName || newName === editingTheme || newName === 'dark' || newName === 'light') return;
    renameCustomTheme(editingTheme, newName);
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    refreshCustomThemes();
    setEditingTheme(newName);
    setShowRename(false);
    setRenameTo('');
  }, [renameTo, editingTheme, refreshCustomThemes]);

  /* ── Delete current custom theme ── */
  const handleDelete = useCallback(() => {
    if (!isCustom) return;
    deleteCustomTheme(editingTheme);
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    refreshCustomThemes();
    setEditingTheme('dark');
  }, [editingTheme, isCustom, refreshCustomThemes]);

  /* ── Reset (built-in themes only) ── */
  const handleResetAll = useCallback(() => {
    clearThemeOverrides(editingTheme);
    loadEditState(editingTheme);
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [editingTheme, loadEditState]);

  /* ── Toggle visibility ── */
  const handleToggleVisibility = useCallback(() => {
    const current = isThemeVisible(editingTheme);
    setThemeVisibility(editingTheme, !current);
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    // Force re-render
    setDirty((d) => d);
  }, [editingTheme]);

  /* ── Save display name / icon ── */
  const handleSaveMetadata = useCallback(() => {
    setThemeMetadata(editingTheme, {
      displayName: editDisplayName.trim() || undefined,
      icon: editIcon.trim() || undefined,
    });
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [editingTheme, editDisplayName, editIcon]);

  /* ── Apply theme to app ── */
  const handleApplyToApp = useCallback(() => {
    applyTheme(editingTheme);
  }, [editingTheme, applyTheme]);

  const currentlyVisible = isThemeVisible(editingTheme);

  // All editable themes: built-in + custom
  const allThemes = useMemo(() => {
    const themes: Array<{ name: string; isCustom: boolean }> = [
      { name: 'dark', isCustom: false },
      { name: 'light', isCustom: false },
      ...customThemes.map((n) => ({ name: n, isCustom: true })),
    ];
    return themes;
  }, [customThemes]);

  return (
    <div>
      <h1 className={styles.pageTitle}>Theme Designer</h1>
      <p className={styles.pageSubtitle}>
        Edit CSS tokens, save as named themes, and control which themes appear in user profiles.
      </p>

      {/* ── Theme selector ── */}
      <div style={{ display: 'flex', gap: 6, margin: '16px 0', flexWrap: 'wrap' as const, alignItems: 'center' }}>
        {allThemes.map((t) => (
          <button
            key={t.name}
            type="button"
            onClick={() => setEditingTheme(t.name)}
            style={{
              padding: '7px 16px', fontSize: 12, fontWeight: editingTheme === t.name ? 700 : 500,
              borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s ease',
              background: editingTheme === t.name ? '#3b82f6' : '#f3f4f6',
              color: editingTheme === t.name ? '#ffffff' : '#6b7280',
              border: editingTheme === t.name ? '1px solid #3b82f6' : '1px solid #d1d5db',
            }}
          >
            {getThemeMetadata(t.name)?.displayName || (t.isCustom ? t.name : (t.name === 'dark' ? 'Dark' : 'Light'))}
            {t.name === activeTheme && ' ✓'}
          </button>
        ))}
        {/* New theme button */}
        <button
          type="button"
          onClick={() => { setShowSaveAs(true); setSaveAsName(''); }}
          style={{
            padding: '7px 14px', fontSize: 12, fontWeight: 600,
            borderRadius: 6, cursor: 'pointer',
            background: '#f3f4f6', color: '#22c55e',
            border: '1px dashed #22c55e',
          }}
        >
          + New Theme
        </button>
      </div>

      {/* ── Save As dialog ── */}
      {showSaveAs && (
        <div style={{
          padding: 16, background: '#f8fafc', border: '1px solid #e5e7eb',
          borderRadius: 8, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>Save As:</span>
          <input
            type="text"
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAs(); if (e.key === 'Escape') setShowSaveAs(false); }}
            placeholder="Theme name..."
            autoFocus
            style={{
              flex: 1, padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db',
              borderRadius: 5, maxWidth: 240,
            }}
          />
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            Based on: {resolveBaseLabel(editingTheme)}
          </span>
          <button type="button" className={styles.btnPrimary} onClick={handleSaveAs}
            disabled={!saveAsName.trim() || saveAsName.trim() === 'dark' || saveAsName.trim() === 'light'}
            style={{ padding: '6px 16px', fontSize: 12 }}>
            Create
          </button>
          <button type="button" className={styles.btnSecondary} onClick={() => setShowSaveAs(false)}
            style={{ padding: '6px 12px', fontSize: 12 }}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Rename dialog ── */}
      {showRename && isCustom && (
        <div style={{
          padding: 16, background: '#f8fafc', border: '1px solid #e5e7eb',
          borderRadius: 8, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>Rename:</span>
          <input
            type="text"
            value={renameTo}
            onChange={(e) => setRenameTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setShowRename(false); }}
            placeholder="New name..."
            autoFocus
            style={{
              flex: 1, padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db',
              borderRadius: 5, maxWidth: 240,
            }}
          />
          <button type="button" className={styles.btnPrimary} onClick={handleRename}
            disabled={!renameTo.trim()} style={{ padding: '6px 16px', fontSize: 12 }}>
            Rename
          </button>
          <button type="button" className={styles.btnSecondary} onClick={() => setShowRename(false)}
            style={{ padding: '6px 12px', fontSize: 12 }}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Action bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' as const }}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={handleSave}
          disabled={!dirty}
          style={{ opacity: dirty ? 1 : 0.5, padding: '7px 18px', fontSize: 12 }}
        >
          Save
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => { setShowSaveAs(true); setSaveAsName(''); }}
          style={{ padding: '7px 14px', fontSize: 12 }}
        >
          Save As...
        </button>
        {editingTheme !== activeTheme && (
          <button
            type="button"
            onClick={handleApplyToApp}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 600,
              background: '#22c55e', color: '#fff', border: '1px solid #22c55e',
              borderRadius: 6, cursor: 'pointer',
            }}
          >
            Apply to App
          </button>
        )}
        {!isCustom && totalOverrides > 0 && (
          <button type="button" className={styles.btnSecondary} onClick={handleResetAll}
            style={{ padding: '7px 14px', fontSize: 12 }}>
            Reset to Defaults
          </button>
        )}
        {isCustom && (
          <>
            <button type="button" className={styles.btnSecondary}
              onClick={() => { setShowRename(true); setRenameTo(editingTheme); }}
              style={{ padding: '7px 14px', fontSize: 12 }}>
              Rename
            </button>
            <button type="button"
              onClick={handleDelete}
              style={{
                padding: '7px 14px', fontSize: 12, fontWeight: 600,
                background: 'rgba(239,68,68,0.08)', color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, cursor: 'pointer',
              }}>
              Delete
            </button>
          </>
        )}
        {/* Visibility toggle */}
        <button type="button"
          onClick={handleToggleVisibility}
          title={currentlyVisible ? 'Visible in user profile dropdown' : 'Hidden from user profile dropdown'}
          style={{
            padding: '7px 14px', fontSize: 12, fontWeight: 500,
            background: currentlyVisible ? 'rgba(34,197,94,0.08)' : '#f3f4f6',
            color: currentlyVisible ? '#16a34a' : '#9ca3af',
            border: `1px solid ${currentlyVisible ? 'rgba(34,197,94,0.2)' : '#d1d5db'}`,
            borderRadius: 6, cursor: 'pointer',
          }}>
          {currentlyVisible ? '● Visible in Profiles' : '○ Hidden from Profiles'}
        </button>

        {/* Status indicators */}
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && (
            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Saved</span>
          )}
          {dirty && !saved && (
            <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>Unsaved</span>
          )}
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            {totalOverrides} modified
          </span>
          {isCustom && (
            <span style={{ fontSize: 10, padding: '2px 8px', background: '#eff6ff', color: '#3b82f6', borderRadius: 4, fontWeight: 600 }}>
              Custom · {resolveBaseLabel(editingTheme)}
            </span>
          )}
        </div>
      </div>

      {/* ── Display Name & Icon ── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16,
        padding: '12px 16px', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
        borderRadius: 10, border: '2px solid #3b82f6',
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.12)',
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', whiteSpace: 'nowrap' as const }}>
          🏷️ Display Name:
        </span>
        <input
          type="text"
          value={editDisplayName}
          onChange={(e) => setEditDisplayName(e.target.value)}
          placeholder={isCustom ? editingTheme : (editingTheme === 'dark' ? 'Dark' : 'Light')}
          style={{
            flex: 1, padding: '5px 10px', fontSize: 12, border: '1px solid #d1d5db',
            borderRadius: 5, maxWidth: 200, background: '#fff', color: '#1a1a2e',
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', whiteSpace: 'nowrap' as const }}>
          Icon:
        </span>
        <input
          type="text"
          value={editIcon}
          onChange={(e) => setEditIcon(e.target.value)}
          placeholder={resolveBaseLabel(editingTheme) === 'light' ? '\u2600\uFE0F' : '\uD83C\uDF19'}
          style={{
            width: 50, padding: '5px 8px', fontSize: 14, border: '1px solid #d1d5db',
            borderRadius: 5, textAlign: 'center' as const, background: '#fff', color: '#1a1a2e',
          }}
        />
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={handleSaveMetadata}
          style={{ padding: '5px 14px', fontSize: 11 }}
        >
          Apply Label
        </button>
        {(editDisplayName || editIcon) && (
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            Preview: {editIcon || (resolveBaseLabel(editingTheme) === 'light' ? '\u2600\uFE0F' : '\uD83C\uDF19')} {editDisplayName || (isCustom ? editingTheme : (editingTheme === 'dark' ? 'Dark' : 'Light'))}
          </span>
        )}
      </div>

      {/* ── Two-column layout: Editor | Preview ── */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {/* ── Left: Token Groups ── */}
        <div style={{ flex: '1 1 55%', minWidth: 0 }}>
          {TOKEN_GROUPS.map((group) => (
            <TokenGroupPanel
              key={group.id}
              group={group}
              editedTokens={editedTokens}
              baseTheme={baseTheme}
              focusedGroupId={focusedGroupId}
              focusedTokens={focusedTokens}
              onChange={handleTokenChange}
              onReset={handleTokenReset}
              onGroupClick={handleGroupFocus}
              groupRef={(el: HTMLDivElement | null) => { groupRefs.current[group.id] = el; }}
              tokenRefs={tokenRowRefs.current}
            />
          ))}

          {/* DEV-1478 — Backgrounds. Which textures this theme offers its users.
              Saved into the theme's existing token_overrides bag under a reserved
              key, so it rides the same persistence every other designer edit uses
              (localStorage → API → theme_overrides.token_overrides JSONB) — no
              schema, no new endpoint. */}
          <BackgroundAllowlistPanel
            themeName={editingTheme}
            baseTheme={resolveBaseLabel(editingTheme)}
          />
        </div>

        {/* ── Right: Live Preview ── */}
        <div style={{ flex: '0 0 360px', position: 'sticky' as const, top: 20 }}>
          <PreviewPanel
            tokens={editedTokens}
            focusedSectionId={focusedSectionId}
            onSectionClick={handlePreviewSectionClick}
          />
        </div>
      </div>
    </div>
  );
}
